/**
 * Context de Notificaciones
 * Wrapper sobre notistack para mostrar notificaciones en toda la aplicación
 */
import { createContext, useContext } from 'react';
import { useSnackbar } from 'notistack';

// Crear contexto
const NotificationContext = createContext(undefined);

// Provider
export const NotificationProvider = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  /**
   * Mostrar notificación de éxito
   * @param {string} message - Mensaje a mostrar
   * @param {Object} options - Opciones adicionales de notistack
   */
  const showSuccess = (message, options = {}) => {
    enqueueSnackbar(message, {
      variant: 'success',
      autoHideDuration: 3000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
      ...options,
    });
  };

  /**
   * Mostrar notificación de error
   * @param {string} message - Mensaje a mostrar
   * @param {Object} options - Opciones adicionales de notistack
   */
  const showError = (message, options = {}) => {
    enqueueSnackbar(message, {
      variant: 'error',
      autoHideDuration: 5000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
      ...options,
    });
  };

  /**
   * Mostrar notificación de advertencia
   * @param {string} message - Mensaje a mostrar
   * @param {Object} options - Opciones adicionales de notistack
   */
  const showWarning = (message, options = {}) => {
    enqueueSnackbar(message, {
      variant: 'warning',
      autoHideDuration: 4000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
      ...options,
    });
  };

  /**
   * Mostrar notificación de información
   * @param {string} message - Mensaje a mostrar
   * @param {Object} options - Opciones adicionales de notistack
   */
  const showInfo = (message, options = {}) => {
    enqueueSnackbar(message, {
      variant: 'info',
      autoHideDuration: 3000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
      ...options,
    });
  };

  /**
   * Cerrar notificación
   * @param {string|number} key - Key de la notificación a cerrar
   */
  const closeNotification = (key) => {
    closeSnackbar(key);
  };

  /**
   * Cerrar todas las notificaciones
   */
  const closeAllNotifications = () => {
    closeSnackbar();
  };

  const value = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeNotification,
    closeAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Hook personalizado
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      'useNotification debe ser usado dentro de un NotificationProvider'
    );
  }
  return context;
};

export default NotificationContext;
